import React, {
  useMemo,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import SlopeCalculator from "../components/SlopeCalculator";
import {
  fetchGolfCourseDetails,
  fetchGolfCourses,
} from "../services/golfCourses";

const VERIFIED_META = {
  label: "Verifierad",
  icon: CheckCircle2Icon,
  className:
    "bg-[#e5ede7] text-[#287148]",
};

const OWN_META = {
  label: "Egen bana",
  icon: UserIcon,
  className:
    "bg-[#eee9dc] text-[#5e625f]",
};

export default function Courses() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [courses, setCourses] =
    useState([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");
  const [scope, setScope] =
    useState("all");
  const [holeFilter, setHoleFilter] =
    useState("all");

  const [
    expandedCourseId,
    setExpandedCourseId,
  ] = useState(null);

  const [
    detailState,
    setDetailState,
  ] = useState({});

  React.useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const result =
          await fetchGolfCourses();

        if (alive) {
          setCourses(result);
        }
      } catch (err) {
        console.error(
          "Kunde inte hämta golfbanor:",
          err
        );

        if (alive) {
          setError(
            err?.message ||
              "Golfbanorna kunde inte hämtas."
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const filteredCourses =
    useMemo(() => {
      const query = search
        .trim()
        .toLocaleLowerCase(
          "sv-SE"
        );

      return courses.filter(
        (course) => {
          const club =
            course.golf_clubs;

          const haystack = [
            course.name,
            club?.name,
            club?.city,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase(
              "sv-SE"
            );

          const matchesSearch =
            !query ||
            haystack.includes(
              query
            );

          const matchesScope =
            scope === "all" ||
            (scope === "mine" &&
              course.created_by ===
                user?.id) ||
            (scope ===
              "verified" &&
              course.approval_status ===
                "approved");

          const matchesHoleFilter =
            holeFilter === "all" ||
            Number(
              course.hole_count
            ) ===
              Number(
                holeFilter
              );

          return (
            matchesSearch &&
            matchesScope &&
            matchesHoleFilter
          );
        }
      );
    }, [
      courses,
      search,
      scope,
      holeFilter,
      user?.id,
    ]);

  const successMessage =
    location.state
      ?.courseUpdated
      ? location.state
          ?.submitted
        ? "Ändringarna har sparats och banan har skickats till HoleInOne."
        : "Ändringarna i din bana har sparats."
      : location.state
          ?.courseCreated
      ? location.state
          ?.submitted
        ? "Banan har sparats och skickats till HoleInOne för granskning."
        : "Banan har sparats som din egen bana."
      : "";

  async function toggleCourse(
    course
  ) {
    if (
      expandedCourseId ===
      course.id
    ) {
      setExpandedCourseId(
        null
      );
      return;
    }

    setExpandedCourseId(
      course.id
    );

    const current =
      detailState[course.id];

    if (
      current?.data ||
      current?.loading
    ) {
      return;
    }

    setDetailState(
      (state) => ({
        ...state,
        [course.id]: {
          loading: true,
          error: "",
          data: null,
        },
      })
    );

    try {
      const data =
        await fetchGolfCourseDetails(
          course.id
        );

      setDetailState(
        (state) => ({
          ...state,
          [course.id]: {
            loading: false,
            error: "",
            data,
          },
        })
      );
    } catch (err) {
      console.error(
        "Kunde inte hämta bandetaljer:",
        err
      );

      setDetailState(
        (state) => ({
          ...state,
          [course.id]: {
            loading: false,
            error:
              err?.message ||
              "Bandetaljerna kunde inte hämtas.",
            data: null,
          },
        })
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#f3f0e7] px-4 py-8 text-[#1f2d26] sm:px-6">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <motion.main
        className="relative mx-auto w-full max-w-6xl"
        initial={{
          opacity: 0,
          y: 14,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.10)]">
          <div className="relative overflow-hidden bg-[#183d2d] px-5 py-7 text-white sm:px-8 sm:py-9">
            <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-[#b08b45]/20 blur-3xl" />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#d7c28e]">
                  Bandatabas
                </p>

                <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                  Golfbanor
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
                  Sök bland verifierade
                  golfbanor eller använd
                  banor som du själv har
                  lagt in.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/courses/new"
                  )
                }
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b08b45] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#9a7735]"
              >
                <PlusIcon className="h-4 w-4" />
                Lägg till bana
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {successMessage && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#bdd5c5] bg-[#edf5ef] px-4 py-3 text-sm text-[#245e3e]">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  {
                    successMessage
                  }
                </span>
              </div>
            )}

            {error && (
              <div className="mb-5 rounded-2xl border border-[#e3beba] bg-[#fbefed] px-4 py-3 text-sm text-[#963f39]">
                {error}
              </div>
            )}

            <div className="relative">
              <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#969b97]" />

              <input
                type="search"
                value={search}
                onChange={(
                  event
                ) =>
                  setSearch(
                    event.target
                      .value
                  )
                }
                placeholder="Sök golfklubb, bana eller ort..."
                className="w-full rounded-2xl border border-[#d8d2c4] bg-white py-3.5 pl-12 pr-4 text-sm outline-none transition placeholder:text-[#9ba09d] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
              />
            </div>

            <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <FilterButton
                  active={
                    scope ===
                    "all"
                  }
                  onClick={() =>
                    setScope(
                      "all"
                    )
                  }
                >
                  Alla banor
                </FilterButton>

                <FilterButton
                  active={
                    scope ===
                    "verified"
                  }
                  onClick={() =>
                    setScope(
                      "verified"
                    )
                  }
                >
                  Verifierade
                </FilterButton>

                <FilterButton
                  active={
                    scope ===
                    "mine"
                  }
                  onClick={() =>
                    setScope(
                      "mine"
                    )
                  }
                >
                  Mina banor
                </FilterButton>
              </div>

              <div className="flex flex-wrap gap-2">
                <FilterButton
                  active={
                    holeFilter ===
                    "all"
                  }
                  onClick={() =>
                    setHoleFilter(
                      "all"
                    )
                  }
                >
                  Alla
                </FilterButton>

                <FilterButton
                  active={
                    holeFilter ===
                    9
                  }
                  onClick={() =>
                    setHoleFilter(9)
                  }
                >
                  9 hål
                </FilterButton>

                <FilterButton
                  active={
                    holeFilter ===
                    18
                  }
                  onClick={() =>
                    setHoleFilter(
                      18
                    )
                  }
                >
                  18 hål
                </FilterButton>
              </div>
            </div>

            <div className="mt-6">
              {loading ? (
                <LoadingState />
              ) : filteredCourses
                  .length ===
                0 ? (
                <EmptyState
                  hasSearch={Boolean(
                    search.trim()
                  )}
                  onCreate={() =>
                    navigate(
                      "/courses/new"
                    )
                  }
                />
              ) : (
                <>
                  <p className="mb-3 text-xs text-[#7b817d]">
                    {
                      filteredCourses.length
                    }{" "}
                    {filteredCourses.length ===
                    1
                      ? "bana"
                      : "banor"}
                  </p>

                  <CourseCollection
                    courses={
                      filteredCourses
                    }
                    userId={user?.id}
                    expandedCourseId={
                      expandedCourseId
                    }
                    detailState={
                      detailState
                    }
                    onToggle={
                      toggleCourse
                    }
                    onEdit={(
                      course
                    ) =>
                      navigate(
                        `/courses/new?edit=${encodeURIComponent(
                          course.id
                        )}`
                      )
                    }
                  />
                </>
              )}
            </div>
          </div>
        </section>
      </motion.main>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition ${
        active
          ? "border-[#183d2d] bg-[#183d2d] text-white"
          : "border-[#d8d2c4] bg-[#fffdf8] text-[#68716c] hover:border-[#b8b09f]"
      }`}
    >
      {children}
    </button>
  );
}

function CourseCollection({
  courses,
  userId,
  expandedCourseId,
  detailState,
  onToggle,
  onEdit,
}) {
  const rows = [];

  for (
    let index = 0;
    index < courses.length;
    index += 2
  ) {
    rows.push(
      courses.slice(
        index,
        index + 2
      )
    );
  }

  const renderCard = (
    course
  ) => {
    const isExpanded =
      expandedCourseId ===
      course.id;

    return (
      <CourseCard
        key={course.id}
        course={course}
        isMine={
          course.created_by ===
          userId
        }
        isExpanded={
          isExpanded
        }
        onToggle={() =>
          onToggle(course)
        }
        onEdit={() =>
          onEdit(course)
        }
      />
    );
  };

  return (
    <>
      <div className="space-y-3 md:hidden">
        {courses.map(
          (course) => (
            <React.Fragment
              key={course.id}
            >
              {renderCard(
                course
              )}

              {expandedCourseId ===
                course.id && (
                <ExpandedCoursePanel
                  course={
                    course
                  }
                  state={
                    detailState[
                      course.id
                    ]
                  }
                />
              )}
            </React.Fragment>
          )
        )}
      </div>

      <div className="hidden space-y-3 md:block">
        {rows.map(
          (
            row,
            rowIndex
          ) => {
            const expandedCourse =
              row.find(
                (course) =>
                  course.id ===
                  expandedCourseId
              );

            return (
              <div
                key={`course-row-${rowIndex}-${row[0]?.id || ""}`}
                className="space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  {row.map(
                    renderCard
                  )}
                </div>

                {expandedCourse && (
                  <ExpandedCoursePanel
                    course={
                      expandedCourse
                    }
                    state={
                      detailState[
                        expandedCourse
                          .id
                      ]
                    }
                  />
                )}
              </div>
            );
          }
        )}
      </div>
    </>
  );
}

function CourseCard({
  course,
  isMine,
  isExpanded,
  onToggle,
  onEdit,
}) {
  const club =
    course.golf_clubs;

  const isVerified =
    course.approval_status ===
    "approved";

  const badge =
    isVerified
      ? VERIFIED_META
      : isMine
      ? OWN_META
      : null;

  const BadgeIcon =
    badge?.icon;

  const teeCount =
    course.golf_course_tees
      ?.length ?? 0;

  const canEdit =
    isMine &&
    [
      "private",
      "rejected",
    ].includes(
      course.approval_status
    );

  return (
    <article
      className={`h-full rounded-2xl border bg-white transition ${
        isExpanded
          ? "border-[#b8b09f] shadow-md"
          : "border-[#d8d2c4] hover:-translate-y-0.5 hover:border-[#b8b09f] hover:shadow-md"
      }`}
    >
      <div className="flex h-full flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e5ede7] text-[#183d2d]">
              <FlagIcon className="h-5 w-5" />
            </span>

            <div className="min-w-0">
              <p className="truncate text-xs font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
                {club?.name ||
                  "Golfklubb"}
              </p>

              <h2 className="mt-1 truncate text-lg font-semibold text-[#183d2d]">
                {course.name}
              </h2>
            </div>
          </div>

          {badge && (
            <span
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${badge.className}`}
            >
              <BadgeIcon className="h-3.5 w-3.5" />
              {badge.label}
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#68716c]">
          <span>
            {course.hole_count}{" "}
            hål
          </span>

          <span>
            {teeCount}{" "}
            {teeCount === 1
              ? "tee"
              : "tees"}
          </span>

          {club?.city && (
            <span className="inline-flex items-center gap-1">
              <MapPinIcon className="h-3.5 w-3.5" />
              {club.city}
            </span>
          )}
        </div>

        <div className="mt-auto pt-4">
          <div className="flex flex-wrap items-center gap-2 border-t border-[#eee9dc] pt-4">
            <button
              type="button"
              onClick={onToggle}
              className="inline-flex items-center gap-2 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-3.5 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#f3f0e7]"
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}

              {isExpanded
                ? "Dölj bandata"
                : "Visa bandata"}
            </button>

            {canEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-2 rounded-xl border border-[#183d2d] px-3.5 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#e5ede7]"
              >
                <PencilIcon className="h-4 w-4" />
                Redigera
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function ExpandedCoursePanel({
  course,
  state,
}) {
  const club =
    course.golf_clubs;

  return (
    <div className="overflow-hidden rounded-2xl border border-[#b8b09f] bg-[#faf8f2] shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e4ded1] bg-white px-4 py-3 sm:px-5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
            Bandata
          </p>

          <p className="mt-0.5 text-sm font-semibold text-[#183d2d]">
            {club?.name
              ? `${club.name} · `
              : ""}
            {course.name}
          </p>
        </div>

        <span className="text-[11px] text-[#858b87]">
          {course.hole_count} hål
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <ExpandedCourseData
          state={state}
        />
      </div>
    </div>
  );
}

function ExpandedCourseData({
  state,
}) {
  if (
    !state ||
    state.loading
  ) {
    return (
      <div className="py-8 text-center text-sm text-[#68716c]">
        Hämtar scorekort och
        slopeinformation…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="rounded-xl border border-[#e3beba] bg-[#fbefed] px-4 py-3 text-sm text-[#963f39]">
        {state.error}
      </div>
    );
  }

  const course =
    state.data;

  if (!course) {
    return null;
  }

  const totalPar =
    getTotalPar(course);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <DetailStat
          label="Par"
          value={
            totalPar || "–"
          }
        />

        <DetailStat
          label="Tees"
          value={
            course.tees
              ?.map(
                (tee) =>
                  tee.name
              )
              .join(", ") ||
            "–"
          }
        />
      </div>

      <SlopeCalculator
        course={course}
      />

      <div>
        <SectionTitle
          title="Course Rating & Slope"
          description="Registrerad rating per tee och kategori."
        />

        <div className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-xs">
              <thead className="bg-[#183d2d] text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">
                    Tee
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Kategori
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    CR
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Slope
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Hål
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Datum
                  </th>
                </tr>
              </thead>

              <tbody>
                {(course.tees ||
                  []).flatMap(
                  (tee) =>
                    (
                      tee.ratings ||
                      []
                    ).map(
                      (
                        rating
                      ) => (
                        <tr
                          key={`${tee.id}-${rating.rating_category}-${rating.start_hole}-${rating.hole_count}`}
                          className="border-t border-[#eee9dc]"
                        >
                          <td className="px-4 py-3 font-semibold text-[#183d2d]">
                            {
                              tee.name
                            }
                          </td>

                          <td className="px-4 py-3">
                            {rating.rating_category ===
                            "men"
                              ? "Herr"
                              : "Dam"}
                          </td>

                          <td className="px-4 py-3">
                            {formatNumber(
                              rating.course_rating
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {formatNumber(
                              rating.slope_rating
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {
                              rating.start_hole
                            }
                            –
                            {Number(
                              rating.start_hole
                            ) +
                              Number(
                                rating.hole_count
                              ) -
                              1}
                          </td>

                          <td className="px-4 py-3">
                            {rating.rating_date ||
                              "–"}
                          </td>
                        </tr>
                      )
                    )
                )}

                {(course.tees ||
                  []).every(
                  (tee) =>
                    (
                      tee.ratings ||
                      []
                    ).length ===
                    0
                ) && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-[#68716c]"
                    >
                      Ingen
                      slopeinformation
                      registrerad.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <SectionTitle
          title="Scorekort"
          description="Par, hålindex och registrerad längd från varje tee."
        />

        <div className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-center text-xs">
              <thead className="bg-[#183d2d] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">
                    Hål
                  </th>
                  <th className="px-3 py-3 font-semibold">
                    Par
                  </th>
                  <th className="px-3 py-3 font-semibold">
                    SI
                  </th>

                  {(course.tees ||
                    []).map(
                    (tee) => (
                      <th
                        key={
                          tee.id
                        }
                        className="px-3 py-3 font-semibold"
                      >
                        {
                          tee.name
                        }
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {(course.holes ||
                  []).map(
                  (hole) => {
                    const meta =
                      getHoleMeta(
                        course,
                        hole.id
                      );

                    return (
                      <tr
                        key={
                          hole.id
                        }
                        className="border-t border-[#eee9dc]"
                      >
                        <td className="px-3 py-3 text-left font-semibold text-[#183d2d]">
                          {
                            hole.hole_number
                          }
                        </td>

                        <td className="px-3 py-3">
                          {meta?.par ??
                            "–"}
                        </td>

                        <td className="px-3 py-3">
                          {meta?.stroke_index ??
                            "–"}
                        </td>

                        {(course.tees ||
                          []).map(
                          (tee) => {
                            const distance =
                              (
                                tee.distances ||
                                []
                              ).find(
                                (
                                  item
                                ) =>
                                  item.hole_id ===
                                  hole.id
                              );

                            return (
                              <td
                                key={
                                  tee.id
                                }
                                className="px-3 py-3"
                              >
                                {distance?.distance_meters
                                  ? `${distance.distance_meters} m`
                                  : "–"}
                              </td>
                            );
                          }
                        )}
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {hasDifferentParOrSi(
        course
      ) && (
        <div className="rounded-xl border border-[#dfcfab] bg-[#fbf5e7] px-4 py-3 text-xs leading-5 text-[#8a6623]">
          Par eller hålindex
          skiljer sig mellan
          registrerade
          tee/kategorier. Tabellen
          ovan visar det första
          registrerade värdet per
          hål; den fullständiga
          informationen finns kvar
          i databasen.
        </div>
      )}
    </div>
  );
}

function getHoleMeta(
  course,
  holeId
) {
  for (
    const tee of
      course.tees || []
  ) {
    const handicap =
      (
        tee.handicaps ||
        []
      ).find(
        (item) =>
          item.hole_id ===
          holeId
      );

    if (handicap) {
      return handicap;
    }
  }

  return null;
}

function getTotalPar(course) {
  return (
    course.holes || []
  ).reduce(
    (sum, hole) => {
      const meta =
        getHoleMeta(
          course,
          hole.id
        );

      return (
        sum +
        Number(
          meta?.par || 0
        )
      );
    },
    0
  );
}

function hasDifferentParOrSi(
  course
) {
  for (
    const hole of
      course.holes || []
  ) {
    const rows =
      (course.tees || [])
        .flatMap(
          (tee) =>
            tee.handicaps ||
            []
        )
        .filter(
          (row) =>
            row.hole_id ===
            hole.id
        );

    const variants =
      new Set(
        rows.map(
          (row) =>
            `${row.par}-${row.stroke_index}`
        )
      );

    if (
      variants.size >
      1
    ) {
      return true;
    }
  }

  return false;
}

function formatNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "–";
  }

  return String(value).replace(
    ".",
    ","
  );
}

function DetailStat({
  label,
  value,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#e0d9cc] bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
        {label}
      </p>

      <p className="mt-2 break-words text-sm font-semibold text-[#183d2d]">
        {value}
      </p>
    </div>
  );
}

function SectionTitle({
  title,
  description,
}) {
  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-[#183d2d]">
        {title}
      </h3>

      <p className="mt-0.5 text-xs leading-5 text-[#7b817d]">
        {description}
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-56 items-center justify-center">
      <div className="text-center">
        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#e5ede7] text-xl">
          ⛳
        </span>

        <p className="mt-3 text-sm font-semibold text-[#183d2d]">
          Hämtar golfbanor…
        </p>
      </div>
    </div>
  );
}

function EmptyState({
  hasSearch,
  onCreate,
}) {
  return (
    <div className="rounded-3xl border border-dashed border-[#cfc8b9] bg-[#f7f4ec] px-6 py-12 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e5ede7] text-[#183d2d]">
        <FlagIcon className="h-6 w-6" />
      </span>

      <h2 className="mt-4 text-lg font-semibold text-[#183d2d]">
        {hasSearch
          ? "Ingen bana matchar din sökning"
          : "Inga banor att visa"}
      </h2>

      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#68716c]">
        Hittar du inte banan
        kan du lägga till den
        själv och använda den
        som en egen bana.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-4 py-3 text-sm font-semibold text-white hover:bg-[#214d39]"
      >
        <PlusIcon className="h-4 w-4" />
        Lägg till bana
      </button>
    </div>
  );
}